// ----------------------------------------------------------------------
// <copyright file="Izenda.ReportPartGridStylingUtil.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//
// </copyright>
// ----------------------------------------------------------------------

/**
 * Report Part Gris Setting component
 */

import { assign, get, flow, find, defaults, placeholder } from 'lodash/fp';
import reportPartGridSetting from './Izenda.ReportPartGridSetting';
import {
  DEFAULT_CELL_STYLE,
  EMPTY_CELL_BACKGROUND_COLOR,
  EMPTY_CELL_COLOR,
  DEFAULT_VALUE_PROPERTIES,
  DEFAULT_PIVOT_GRID_CELL_STYLE
} from 'logic/data/Izenda.ReportPartGridConstants';
import reportPartUtils from 'share/utils/Izenda.ReportPartUtils';
import ProcessData from 'logic/Izenda.ReportPartGridProcessData';

const DEFAULT_VERTICAL_ALIGNMENT = DEFAULT_VALUE_PROPERTIES.VERTICAL_ALIGNMENT;

/**
   * get border style for Pivot header
   * @param  {Object} columnData
   * @param  {Object} columnLocInfo
   * @param  {Object} border
   * @return {Object} style of border
   */
function getBorderStyle(columnData, columnLocInfo, border) {
  let style = {};
  const borderColor = columnData.isColumnHeader && columnData.canRenderHeader ? EMPTY_CELL_BACKGROUND_COLOR : null;
  // Border top for first level only
  if (border.top && columnLocInfo.deep === 1 && !columnData.hideTopLeftBorder) {
    style = assign(style, reportPartGridSetting.getBorderStyle('Top', border.top, borderColor));
  }

  // Border middle if it not first level
  if (border.midHor && columnLocInfo.deep !== 1 && !columnData.hideTopLeftBorder) {
    style = assign(style, reportPartGridSetting.getBorderStyle('Top', border.midHor, borderColor));
  }

  // Border left
  if (border.left && columnLocInfo.firstColumn && !columnData.hideTopLeftBorder) {
    style = assign(style, reportPartGridSetting.getBorderStyle('Left', border.left, borderColor));
  }

  // Border mid vertical
  if (border.midVer && !columnLocInfo.firstColumn) {
    style = assign(style, reportPartGridSetting.getBorderStyle('Left', border.midVer, borderColor));
  }

  if (border.right && columnLocInfo.lastColumn && !columnData.hideTopLeftBorder) {
    style = assign(style, reportPartGridSetting.getBorderStyle('Right', border.right, borderColor));
  }
  return style;
}

export default {
  /**
   * get Cell style for Pivot header
   * @param  {Object} columnData
   * @param  {Object} columnLocInfo
   * @param  {Object} border
   * @return {Object} style of header
   */
  getCellStyleForPivotHeader(columnData, columnLocInfo) {
    const { reportPart, records } = this.props;
    const properties = reportPart.getReportPartProperties();
    const border = get('border', properties.table);
    const reportPartHeaderProperties = properties.headers;
    let stylePivotHeader = { ...DEFAULT_PIVOT_GRID_CELL_STYLE };

    if (columnData.isColumnHeader) {
      //const borderStyle = getBorderStyle(columnData, columnLocInfo, border);
      let backgroundColor = get('font.backgroundColor', reportPartHeaderProperties);
      return columnData.canRenderHeader
        ? { backgroundColor: backgroundColor, ...stylePivotHeader, ...getBorderStyle(columnData, columnLocInfo, border) }
        : { backgroundColor: backgroundColor, ...stylePivotHeader };
    }

    const borderStyle = getBorderStyle(columnData, columnLocInfo, border);
    stylePivotHeader = assign(stylePivotHeader, borderStyle);

    let backGroundColor = '';
    let color = '';
    let wordWrapStyle = {};
    let verticalAlign = '';
    if (columnData.isColumnGroup) {
      if (columnData.value) {
        backGroundColor = get('font.backgroundColor', reportPartHeaderProperties);
        color = get('font.color', reportPartHeaderProperties);
        verticalAlign = get('verticalAlignment', reportPartHeaderProperties);
        wordWrapStyle = get('wordWrap', reportPartHeaderProperties)
          ? {
              wordWrap: 'break-word',
              whiteSpace: 'pre-line'
            }
          : {
              wordWrap: 'normal',
              whiteSpace: 'pre'
            };
      } else {
        backGroundColor = EMPTY_CELL_BACKGROUND_COLOR;
        color = EMPTY_CELL_COLOR;
      }
    } else if (columnData.element) {
      const element = columnData.element;
      const defaultBackGroundColor = columnData.element.properties.getDefaultsHeaderProperty(
        'font.backgroundColor',
        reportPartHeaderProperties
      );
      const defaultColor = columnData.element.properties.getDefaultsHeaderProperty(
        'font.color',
        reportPartHeaderProperties
      );

      const rawRecord = element.properties.hasRangePercent
        ? ProcessData.buildRawRecordFilterForPivotGrid(columnData, records)[0]
        : {};

      const dataSource = {
        element: {
          columnName: flow(
            find({ fieldNameAlias: element.field.fieldNameAlias }),
            get('columnName')
          )(reportPart.previewDataFieldsMapping),
          element
        },
        record: rawRecord
      };

      backGroundColor =
        reportPartUtils.getConfigDataByRange(
          columnData.title,
          element.properties.dataFormattings.color.cellColor,
          dataSource
        ) || defaultBackGroundColor;
      color =
        reportPartUtils.getConfigDataByRange(
          columnData.title,
          element.properties.dataFormattings.color.textColor,
          dataSource
        ) || defaultColor;

      verticalAlign = element.properties.dataFormattings.verticalAlignment || properties.columns.verticalAlignment;

      if (columnData.element.properties.getDefaultsHeaderProperty('font.wordWrap', reportPartHeaderProperties)) {
        wordWrapStyle = {
          wordWrap: 'break-word',
          whiteSpace: 'pre-line'
        };
      } else {
        wordWrapStyle = {
          wordWrap: 'normal',
          whiteSpace: 'pre'
        };
      }
    } else {
      backGroundColor = properties.table && properties.table.backgroundColor;
      color = properties.table && properties.table.color;
    }

    return flow(
      defaults(placeholder, {
        color: color,
        background: backGroundColor,
        verticalAlign
      }),
      defaults(placeholder, wordWrapStyle)
    )(stylePivotHeader);
  },

  /**
   * Create style object
   * @param  {Object} cellStyle
   * @return {Object}
   */
  createStyleObject(cellStyle: any): any {
    return defaults(DEFAULT_CELL_STYLE, {
      fontFamily: cellStyle.fontFamily,
      fontSize: `${cellStyle.fontSize}px`,
      fontWeight: cellStyle.bold ? 'bold' : 'normal',
      textAlign: cellStyle.alignment || 'none',
      textDecoration: cellStyle.underline ? 'underline' : 'none',
      fontStyle: cellStyle.italic ? 'italic' : 'normal',
      color: cellStyle.textColor || '',
      wordWrap: cellStyle.wordWrap ? 'break-word' : 'normal',
      whiteSpace: cellStyle.wordWrap ? 'pre-line' : 'pre'
    });
  },

  /**
   * get Cell style setting
   * @param  {Object} reportPart
   * @param  {Object} columnData
   * @param  {Object} rowIndex
   * @param  {Object} columnIndex
   * @return {Object} cellStyleSetting
   */
  getCellStyleSetting(reportPart, columnLocInfo, rowIndex, columnIndex, numberOfRow, isHeader) {
    let borderStyle = {};
    let background;
    const properties = reportPart.getReportPartProperties();
    const border = get('border', properties.table);
    const getBorderStyle = reportPartGridSetting.getBorderStyle;
    const alternativeBackgroundColor = get('alternativeBackgroundColor', properties.table);
    const alternativebackgroundColorColumn = get('alterBackgroundColor', properties.columns) ? 1 : 0;
    const alternativebackgroundColorRow = get('alterBackgroundColor', properties.rows) ? 1 : 0;
    const tableBackgroundColor = get('backgroundColor', properties.table);
    const headerColor = get('backgroundColor', properties.headers.font);
    const headerVerticalAlign = get('verticalAlignment', properties.headers);
    const rowsHeight = get('height', properties.rows);

    /* Border setting*/
    // Border top
    if (border.top && isHeader) {
      borderStyle = assign(borderStyle, getBorderStyle('Top', border.top));
    }

    // Border bottom
    if (border.bottom && rowIndex === numberOfRow) {
      borderStyle = assign(borderStyle, getBorderStyle('Bottom', border.bottom));
    }

    // Border mid vertical
    if (border.midVer && !columnLocInfo.firstColumn) {
      borderStyle = assign(borderStyle, getBorderStyle('Left', border.midVer));
    }

    // Border left
    if (border.left && columnLocInfo.firstColumn) {
      borderStyle = assign(borderStyle, getBorderStyle('Left', border.left));
    }

    // Border right
    if (border.right && columnLocInfo.lastColumn) {
      borderStyle = assign(borderStyle, getBorderStyle('Right', border.right));
    }

    // Border mid Horizontal
    if (border.midHor && (rowIndex < numberOfRow || isHeader)) {
      borderStyle = assign(borderStyle, getBorderStyle('Bottom', border.midHor));
    }

    /* Color setting*/
    if (tableBackgroundColor) {
      background =
        (columnIndex * alternativebackgroundColorColumn + rowIndex * alternativebackgroundColorRow) % 2 === 0
          ? tableBackgroundColor
          : alternativeBackgroundColor;
    }

    return defaults(borderStyle, {
      backgroundColor: isHeader ? headerColor : background,
      verticalAlign: isHeader ? headerVerticalAlign : DEFAULT_VERTICAL_ALIGNMENT,
      height: rowsHeight
    });
  },

  /**
   * get Data Formattings (font/alignment, Color/BackGroundColor, Alternating Text)
   * @param  {Object} element
   * @param  {String} value
   * @return {Object} cellStyleSetting
   */
  getDataFormattings(element, value, rawRecord, reportPart) {
    const styleObject = this.createStyleObject(element.properties.getUIStyle());
    const colorSetting = this.getColorSetting(element, value, rawRecord, reportPart);

    return defaults(styleObject, colorSetting);
  },

  /**
   * get Color/BackGroundColor setting
   * @param  {Object} element
   * @param  {String} value
   * @return {Object} colorSetting
   */
  getColorSetting(element, value, rawRecord, reportPart) {
    const dataSource = {
      element: reportPartUtils.buildDataSourceElement(reportPart, element),
      record: rawRecord[0]
    };

    return {
      backgroundColor: reportPartUtils.getConfigDataByRange(
        value,
        element.properties.dataFormattings.color.cellColor,
        dataSource
      ),
      color: reportPartUtils.getConfigDataByRange(
        value,
        element.properties.dataFormattings.color.textColor,
        dataSource)
    };
  }
};



// WEBPACK FOOTER //
// ./ui/modules/ReportDesigner/Fields/ReportBody/ReportPartType/ReportPartGrid/Izenda.ReportPartGridStylingUtil.js